import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import useAccessBadge from '../../../hooks/useAccessBadge';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import useGeoLocation from '../../../hooks/useGeoLocation';
import { FaqSection } from '../../_molecules/FaqSection';
import { NextPageWithLayout } from '../../layouts';
import { WallOfLoveSection } from '../WallOfLoveSection';
import { CompareFeaturesSection } from './CompareFeaturesSection';
import { FinalCtaSection } from './FinalCtaSection';
import { PlanOverviewSection } from './PlanOverviewSection';

const PricingPageContent: NextPageWithLayout = () => {
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const session = useSession();
  const showIndividualPremiumUnauthenticated = useFeatureFlag(
    'show_individual_premium_unauthenticated',
  );
  const internationalIndividualPremium = useFeatureFlag(
    'international_individual_premium',
  );
  const isAuthenticated = session.status === 'authenticated';
  const { isUS, isLoading: isGeoLoading, error: geoError } = useGeoLocation();

  // Every input below (session, geo, feature flags, access badge) only ever
  // resolves client-side — this flips true after the first client render so
  // the individual Premium plan can never appear in the server-rendered HTML
  // or the initial hydration pass, only once real data is in.
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  // Wait for geo loading to finish before determining whether to show individual premium
  // This prevents flashing of the individual premium plan for US users
  // If geo lookup failed, treat as US (conservative default) to avoid incorrectly showing individual premium
  const showIndividualPremium =
    isClient &&
    !isGeoLoading &&
    !geoError &&
    !!internationalIndividualPremium &&
    ((!isAuthenticated && !isUS && showIndividualPremiumUnauthenticated) ||
      !!userAccessBadge?.canPurchaseIndividualPremium);

  const isUSPricing = isAuthenticated
    ? userAccessBadge?.organization?.country === 'US'
    : isUS;

  return (
    <Container>
      <PlanOverviewSection
        showIndividualPremium={showIndividualPremium}
        isUSPricing={isUSPricing}
      />
      <CompareFeaturesSection showIndividualPremium={showIndividualPremium} />
      <WallOfLoveSection fullBleed={false} />
      <FaqSection variant="light" />
      <FinalCtaSection />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

export default PricingPageContent;
